import { useMemo, useState } from 'react';
import type { PowerCurveWindow } from '../../api/keys';
import { usePowerCurve } from '../../api/queries';
import { EChart } from '../../components/EChart';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { ToggleTabs } from '../../components/ToggleTabs';
import { useVariant } from '../../ui/useMediaQuery';
import { beatenPoints, CURVE_HEIGHT, CURVE_HEIGHT_PHONE, curveSummary, POWER_CURVE_LABEL, powerCurveOption } from './powerCurveOption';
import styles from './analysis.module.css';

const WINDOWS: readonly { value: PowerCurveWindow; label: string }[] = [
  { value: '90d', label: '90 DAYS' },
  { value: 'all', label: 'ALL TIME' },
];
const NOTE: Record<PowerCurveWindow, string> = { '90d': 'BEST OF THE 90 DAYS BEFORE THIS RIDE', all: 'BEST OF EVERY EARLIER RIDE' };

function CurveLegend({ newBests }: { newBests: boolean }) {
  return (
    <ul className={styles.curveLegend} aria-label="Power curve legend">
      <li><span className={styles.curveSwatch} data-series="ride" aria-hidden="true" />THIS RIDE</li>
      <li><span className={styles.curveSwatch} data-series="best" aria-hidden="true" />YOUR BEST</li>
      {newBests && <li><span className={styles.curveSwatch} data-series="marker" aria-hidden="true" />NEW BEST</li>}
    </ul>
  );
}

export function PowerCurveCard({ activityId }: { activityId: string }) {
  const [compareWindow, setCompareWindow] = useState<PowerCurveWindow>('90d');
  const curve = usePowerCurve(activityId, compareWindow);
  const variant = useVariant();
  const height = variant === 'phone' ? CURVE_HEIGHT_PHONE : CURVE_HEIGHT;
  const option = useMemo(() => (curve.data === undefined ? null : powerCurveOption(curve.data, variant)), [curve.data, variant]);
  return (
    <section className={styles.card} data-tone="strong" aria-labelledby="curve-title">
      <header className={styles.curveHead}>
        <div className={styles.curveHeadText}>
          <h2 id="curve-title" className={styles.cardTitle}>POWER CURVE</h2>
          {curve.data !== undefined && <p className={styles.curveSub}>{`${NOTE[curve.data.window]} · ${curveSummary(curve.data)}`}</p>}
        </div>
        <ToggleTabs label="Compare against" options={WINDOWS} value={compareWindow} onChange={setCompareWindow} />
      </header>
      {curve.isPending && <LoadingBlock label="LOADING POWER CURVE" height={height} />}
      {curve.isError && <ErrorPanel error={curve.error} onRetry={() => void curve.refetch()} title="POWER CURVE UNAVAILABLE" />}
      {option !== null && <EChart option={option} height={height} label={POWER_CURVE_LABEL} />}
      {curve.data !== undefined && <CurveLegend newBests={beatenPoints(curve.data).length > 0} />}
    </section>
  );
}
